export const SECTION_NAME = "Process";

export const INTRO_TEXT = `Processes in SOS Inventory can have an unlimited
  number of inputs and outputs. This gives you the flexibility to handle not
  only simple manufacturing (multiple inputs into one output) but also
  disassembly (where one input produces many outputs) and processes with
  by-products (multiple inputs into multiple outputs).`;

export const EXAMPLE_JSON = `{ 
  "id":123,
  "starred":0,
  "syncToken":0,
  "number":"1001",
  "date":"2019-10-02T17:27:00",
  "startDate":"",
  "location":{ 
    "id":1,
    "name":"Texas"
  },
  "job":null,
  "workcenter":{ 
    "id":1,
    "name":"Finished Goods"
  },
  "department":null,
  "linkedTransaction":null,
  "comment":"",
  "customFields":[ 
    { 
      "id":5,
      "name":"Checkbox",
      "value":"false",
      "dataType":"Boolean"
    }
  ],
  "total":0.0,
  "archived":false,
  "summaryOnly":false,
  "hasSignature":false,
  "template":null,
  "multiplier":null,
  "outputs":[ 
    { 
      "id":453,
      "lineNumber":1,
      "item":{ 
        "id":1174,
        "name":"widget"
      },
      "class":null,
      "job":null,
      "workcenter":null,
      "worker":null,
      "tax":null,
      "linkedTransaction":null,
      "description":"",
      "notes":"",
      "quantity":1.00000,
      "weight":10.0000000000,
      "volume":7.0000000000,
      "weightunit":"lb",
      "volumeunit":"cbm",
      "waste":false,
      "uom":null,
      "bin":null,
      "lot":null,
      "lotExpiration":null,
      "serials":[]
    }
  ],
  "inputs":[ 
    { 
      "id":530,
      "lineNumber":1,
      "item":{ 
        "id":1179,
        "name":"Input 1"
      },
      "class":null,
      "job":null,
      "workcenter":null,
      "worker":null,
      "tax":null,
      "linkedTransaction":null,
      "description":null,
      "notes":"",
      "quantity":1.00000,
      "weight":0.0000000000,
      "volume":0.0000000000,
      "weightunit":"lb",
      "volumeunit":"cbm",
      "waste":false,
      "uom":null,
      "bin":null,
      "lot":null,
      "lotExpiration":null,
      "serials":[]
    },
    { 
      "id":531,
      "lineNumber":2,
      "item":{ 
        "id":1180,
        "name":"Input 2"
      },
      "class":null,
      "job":null,
      "workcenter":null,
      "worker":null,
      "tax":null,
      "linkedTransaction":null,
      "description":null,
      "notes":"",
      "quantity":1.00000,
      "weight":0.0000000000,
      "volume":0.0000000000,
      "weightunit":"lb",
      "volumeunit":"cbm",
      "waste":false,
      "uom":null,
      "bin":null,
      "lot":null,
      "lotExpiration":null,
      "serials":[ ]
    }
  ]
}`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. Must not be provided on create transactions.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The process number for this record.  If you wish to use the automatic numbering capability 
    on creation of an process, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "startDate",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Start date for this transaction.`,
  },
  {
    name: "location",
    type: "reference",
    description: `Location for this transaction.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "department",
    type: "reference",
    description: `The department for this transaction.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "comment",
    type: "string",
    description: `Comment field about transaction. For in-house use.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "total",
    type: "number",
    description: "Total inputs on this process.",
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `True if the summary parameter was set when this record was retrieved. False if not.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "boolean",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "template",
    type: "reference",
    description: `The template used to create this transaction.  The API currently does not support creating a transaction from a template.`,
  },
  {
    name: "multiplier",
    type: "decimal",
    description: `The multiplier factor used to modify this transaction.  Setting this value does not change anything on this transaction.
    It is used only as a reference for the multiplier used.`,
    readOnly: true,
  },
  {
    name: "outputs",
    type: "array of manufacturing line items",
    description: `The output line for this process. See object structure below.`,
  },
  {
    name: "inputs",
    type: "array of manufacturing line items",
    description: `The input lines for this process. See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this process line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the process transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "worker",
    type: "reference",
    description: `The worker assigned to this line.`,
  },
  {
    name: "tax",
    type: "tax information object",
    description: "Unused.",
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "notes",
    type: "string",
    description: "Unused.",
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity for this line.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "waste",
    type: "boolean",
    description: "True if this process produces waste, false if not.",
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `The bin used for this item process.`,
  },
  {
    name: "lot",
    type: "reference",
    description: `The lot used for this item process.`,
  },
  {
    name: "serials",
    type: "array of references",
    description: `The serial numbers used for this item process.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the estimate will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, id, comment, location name, or process name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "location",
    type: "string",
    description: `Filters transactions according to the name of the location.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/process?start=101&maxresults=100&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&location=warehouse&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
